import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Paper, 
  Grid, 
  Card, 
  CardContent, 
  CardActions,
  Button,
  Chip,
  Avatar,
  IconButton,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  Divider,
  CircularProgress,
  Snackbar,
  Alert,
  Tooltip,
  AvatarGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { 
  MoreVert as MoreVertIcon, 
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Add as AddIcon,
  TodayOutlined as TodayIcon,
  CalendarToday as CalendarIcon,
  Group as GroupIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Lightbulb as LightbulbIcon,
  EditNote as EditNoteIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ProjectDetailsDialog from './viewproject';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const AllProjects = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [starredProjects, setStarredProjects] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuProject, setMenuProject] = useState(null);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [lastNotificationCheck, setLastNotificationCheck] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [userRole, setUserRole] = useState('');
  const [reasonModalProject, setReasonModalProject] = useState(null);
  const [reasonText, setReasonText] = useState('');
  const [savingReason, setSavingReason] = useState(false);

  useEffect(() => {
    const roleFromStorage = localStorage.getItem('role');
    if (roleFromStorage) {
      setUserRole(roleFromStorage);
    } else {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          setUserRole(payload.role || '');
        } catch (e) {
        }
      }
    }
  }, []);

  const roleClean = String(userRole || localStorage.getItem('role') || '').toLowerCase().trim();
  
  // Can give/edit reason ONLY for Employee, Project Manager, Product Owner, Product Manager (NOT CPO)
  const canGiveReason = [
    'employee',
    'project-manager',
    'project_manager',
    'project manager',
    'product-owner',
    'product_owner',
    'product owner',
    'product-manager',
    'product_manager',
    'product manager'
  ].includes(roleClean) && roleClean !== 'cpo';

  const isProjectManager = [
    'project-manager',
    'admin',
    'product-manager',
    'product-owner',
    'product_manager',
    'product_owner',
    'cpo',
    'employee'
  ].includes(roleClean);

  const handleOpenReasonModal = (e, project) => {
    e.stopPropagation();
    setReasonModalProject(project);
    setReasonText(project.reason || project.notes || '');
  };

  const handleCloseReasonModal = () => {
    setReasonModalProject(null);
    setReasonText('');
  };

  const handleSaveReason = async () => {
    if (!reasonModalProject) return;
    try {
      setSavingReason(true);
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_URL}/api/projects/${reasonModalProject.id}/reason`,
        { reason: reasonText, notes: reasonText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data && response.data.status === 'success') {
        setProjects(prevProjects =>
          prevProjects.map(p =>
            p.project_id === reasonModalProject.id ? { ...p, reason: reasonText, notes: reasonText } : p
          )
        );
        setSnackbar({
          open: true,
          message: 'Delay/Risk reason saved successfully!',
          severity: 'success'
        });
        handleCloseReasonModal();
      } else {
        throw new Error(response.data?.message || 'Failed to save reason');
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Failed to save reason: ' + (err.message || 'Unknown error'),
        severity: 'error'
      });
    } finally {
      setSavingReason(false);
    }
  };
  
  // Helper function to get consistent avatar color based on project key
  const getAvatarColor = (key) => {
    const colors = [
      '#2196F3', // Blue
      '#4CAF50', // Green
      '#FF9800', // Orange
      '#E91E63', // Pink
      '#9C27B0', // Purple
      '#00BCD4', // Cyan
      '#F44336', // Red
      '#3F51B5'  // Indigo
    ];
    
    let hash = 0;
    if (!key) return colors[0];
    
    // Simple hash function to get consistent color
    for (let i = 0; i < key.length; i++) {
      hash = key.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    return colors[Math.abs(hash) % colors.length];
  };

  // Helper function to check project status based on dates
  const getProjectDateStatus = (project) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const startDate = project.start_date ? new Date(project.start_date) : null;
    const endDate = project.end_date ? new Date(project.end_date) : null;
    
    if (startDate) startDate.setHours(0, 0, 0, 0);
    if (endDate) endDate.setHours(0, 0, 0, 0);
    
    // Skip if project is already completed
    if (project.status === 'Completed') {
      return { status: 'completed', message: null };
    }
    
    // Check if project is overdue
    if (endDate && endDate < today) {
      const daysOverdue = Math.floor((today - endDate) / (1000 * 60 * 60 * 24));
      return { 
        status: 'overdue', 
        message: `Overdue by ${daysOverdue} ${daysOverdue === 1 ? 'day' : 'days'}`,
        severity: 'error',
        days: daysOverdue
      };
    }

    const progress = calculateProgress(project);

    // Check if project is At Risk (Progress < 95% with 5 or fewer days remaining, or explicit At Risk status)
    if (endDate && endDate >= today) {
      const daysUntilEnd = Math.floor((endDate - today) / (1000 * 60 * 60 * 24));
      if (daysUntilEnd <= 5 && progress < 95) {
        return { 
          status: 'at-risk', 
          message: `At Risk (${progress}% done, ${daysUntilEnd}d left)`,
          severity: 'warning',
          days: daysUntilEnd
        };
      }
    }

    if (String(project.status || '').toLowerCase().includes('risk')) {
      return {
        status: 'at-risk',
        message: 'At Risk: Attention Needed',
        severity: 'warning'
      };
    }
    
    // Check if project is ending soon (within 7 days)
    if (endDate && endDate >= today) {
      const daysUntilEnd = Math.floor((endDate - today) / (1000 * 60 * 60 * 24));
      if (daysUntilEnd <= 7) {
        return { 
          status: 'ending-soon', 
          message: `Ending in ${daysUntilEnd} ${daysUntilEnd === 1 ? 'day' : 'days'}`,
          severity: 'warning',
          days: daysUntilEnd
        };
      }
    }
    
    // Check if project is starting soon (within 3 days)
    if (startDate && startDate >= today) {
      const daysUntilStart = Math.floor((startDate - today) / (1000 * 60 * 60 * 24));
      if (daysUntilStart <= 3 && daysUntilStart >= 0) {
        return { 
          status: 'starting-soon', 
          message: `Starting in ${daysUntilStart} ${daysUntilStart === 1 ? 'day' : 'days'}`,
          severity: 'info',
          days: daysUntilStart
        };
      }
    }
    
    return { status: 'on-track', message: null };
  };

// Helper function to calculate progress - FIXED VERSION (NEW PROJECTS START AT 0)
const calculateProgress = (project) => {
  // Check if this is a new project (created in last 5 minutes and no tasks)
  const createdAt = new Date(project.createdAt || project.created_at);
  const now = new Date();
  const isNewProject = (now - createdAt) < (5 * 60 * 1000); // 5 minutes
  
  // Force 0% for new projects with no tasks and no completion percentage
  if (isNewProject && 
      (!project.tasks || project.tasks.length === 0) && 
      (project.completionPercentage === undefined || project.completionPercentage === null)) {
    return 0;
  }
  
  // Priority 1: Use the completionPercentage if it exists and is valid
  if (project.completionPercentage !== undefined && 
      project.completionPercentage !== null && 
      !isNaN(project.completionPercentage)) {
    return Math.max(0, Math.min(100, parseInt(project.completionPercentage)));
  }
  
  // Priority 2: Calculate from tasks if available
  if (project.tasks && Array.isArray(project.tasks) && project.tasks.length > 0) {
    const completedTasks = project.tasks.filter(task => 
      task.status === 'Completed' || task.status === 'Done'
    ).length;
    return Math.floor((completedTasks / project.tasks.length) * 100);
  }
  
  // Default return 0 for all other cases
  return 0;
};

  // Helper function to determine priority
  const determinePriority = (project) => {
    if (project.priority) return project.priority;
    
    // Assign priorities based on project attributes
    if (project.end_date && new Date(project.end_date) < new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)) return 'High';
    if (project.status === 'Active') return 'Medium';
    
    const priorities = ['High', 'Medium', 'Low'];
    const hash = project.project_id ? parseInt(project.project_id, 10) : 0;
    return priorities[hash % 3];
  };
  
  // Check for project notifications (overdue, ending soon, starting soon)
  const checkProjectNotifications = async () => {
    try {
      // Check if we've checked in the last 30 minutes to avoid too many calls
      if (lastNotificationCheck && (Date.now() - lastNotificationCheck) < 30 * 60 * 1000) {
        return;
      }

      const token = localStorage.getItem('token');
      if (!token) return;

      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;

      const response = await axios.get(
        `${API_URL}/api/projects/check-notifications`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.data.success && response.data.data.warningsFound > 0) {
        
        // Show notification to the user
        setSnackbar({
          open: true,
          message: `${response.data.data.warningsFound} project(s) need attention. Check the highlighted cards.`,
          severity: 'warning'
        });
      }

      setLastNotificationCheck(Date.now());
    } catch (error) {
    }
  };

  // Fetch projects from the backend
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          alert("No authentication token found. Please log in.");
          return;
        }
      
        const payload = JSON.parse(atob(token.split('.')[1]));
        const company_id = payload.company_id;
        setLoading(true);
        const response = await axios.get(`${API_URL}/api/projects`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            withCredentials: true
          }
        );
        if (response.data.status === "success") {
          setProjects(response.data.data);
          
          // Load starred projects from local storage
          const storedStarred = localStorage.getItem('starredProjects');
          if (storedStarred) {
            setStarredProjects(new Set(JSON.parse(storedStarred)));
          }
        } else {
          setError('Failed to load projects');
        }
      } catch (err) {
        setError(err.message || 'An error occurred while fetching projects');
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  // Check for notifications on component mount and every hour
  useEffect(() => {
    checkProjectNotifications();
    
    const interval = setInterval(() => {
      checkProjectNotifications();
    }, 60 * 60 * 1000); // Check every hour
    
    return () => clearInterval(interval);
  }, []);
  
  // Format project data for display - UPDATED
  const formatProjects = (projectsData) => {
    return projectsData.map(project => {
      const progress = calculateProgress(project);
      const priority = determinePriority(project);
      const dateStatus = getProjectDateStatus(project);
      
      return {
        id: project.project_id,
        name: project.name,
        key: project.key,
        type: project.projectType,
        description: project.description,
        notes: project.notes || '',
        reason: project.reason || project.notes || '',
        lead: project.projectLead ? project.projectLead.name : 'Unassigned',
        leadAvatar: project.projectLead?.avatar || null,
        projectManagers: project.projectManagers ? project.projectManagers.map(pm => pm.name).join(', ') : '',
        starred: starredProjects.has(project.project_id),
        status: project.status || 'Not Started',
        lastUpdated: formatDate(project.updatedAt || project.createdAt),
        teamMembers: project.teamMembers || [],
        startDate: project.start_date,
        endDate: project.end_date,
        budget: project.budget,
        costCenterId: project.costCenterId,
        product_manager_id: project.product_manager_id,
        product_owner_id: project.product_owner_id,
        avatar_bg: getAvatarColor(project.key),
        progress: progress, // This is the calculated progress
        completionPercentage: progress, // Make sure both properties have the same value
        priority: priority,
        dateStatus: dateStatus,
        tasks: {
          total: project.tasks?.length || 0,
          completed: project.tasks?.filter(task => 
            task.status === 'Completed' || task.status === 'Done'
          ).length || 0
        },
        team: project.teamMembers?.map(member => ({
          id: member.employee_id || member.id,
          name: member.name,
          avatar: member.avatar || null
        })) || []
      };
    });
  };
  
  // Format date as "X days/hours ago"
  const formatDate = (dateString) => {
    if (!dateString) return 'Recently';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
    
    if (diffDays > 0) {
      return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
    } else {
      return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
    }
  };
  
  // Format date for display
  const formatDisplayDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString();
  };
  
  // Handle project menu
  const handleMenuOpen = (event, project) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setMenuProject(project);
  };
  
  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuProject(null);
  };
  
  // Handle filter menu
  const handleFilterMenuOpen = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };
  
  const handleFilterMenuClose = (type = null) => {
    if (type) {
      setFilterType(type);
    }
    setFilterAnchorEl(null);
  };
  
  // Toggle star for a project
  const toggleStar = (id) => {
    const newStarred = new Set(starredProjects);
    if (newStarred.has(id)) {
      newStarred.delete(id);
    } else {
      newStarred.add(id);
    }
    setStarredProjects(newStarred);
    
    // Save to local storage
    localStorage.setItem('starredProjects', JSON.stringify([...newStarred]));
  };
  
  // Handle search
  const handleSearch = (event) => {
    setSearchText(event.target.value);
  };
  
  // Handle viewing project details
  const handleViewProject = (projectId) => {
    setSelectedProjectId(projectId);
    setDialogOpen(true);
  };

  // Handler for closing project details dialog
  const handleCloseDialog = () => {
    setDialogOpen(false);
  };
  
  // Navigate to edit project page
  const handleEditProject = (project) => {
    // Store project data in localStorage to pass to the edit form
    localStorage.setItem('editProject', JSON.stringify(project));
    navigate(`/create-project?edit=true&id=${project.id}`);
  };
  
  // Delete a project
  const handleDeleteProject = async (id) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }
    
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      const response = await axios.delete(`${API_URL}/api/projects/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.data.status === "success") {
        setProjects(projects.filter(project => project.project_id !== id));
        setSnackbar({
          open: true,
          message: 'Project deleted successfully',
          severity: 'success'
        });
      } else {
        setSnackbar({
          open: true,
          message: 'Failed to delete project',
          severity: 'error'
        });
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || 'An error occurred while deleting project',
        severity: 'error'
      });
    }
    handleMenuClose();
  };
  
  // Close snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };
  
  // Apply filters and sorting
  const applyFiltersAndSort = () => {
    let filteredProjects = [...formatProjects(projects)];
    
    // Apply search filter
    if (searchText) {
      filteredProjects = filteredProjects.filter(project => 
        project.name.toLowerCase().includes(searchText.toLowerCase()) ||
        project.key.toLowerCase().includes(searchText.toLowerCase())
      );
    }
    
    // Apply category filter
    switch (filterType) {
      case 'starred':
        filteredProjects = filteredProjects.filter(project => project.starred);
        break;
      case 'active':
        filteredProjects = filteredProjects.filter(project => project.status === 'Active');
        break;
      case 'completed':
        filteredProjects = filteredProjects.filter(project => project.status === 'Completed');
        break;
      case 'planning':
        filteredProjects = filteredProjects.filter(project => project.status === 'Planning');
        break;
      case 'on-hold':
        filteredProjects = filteredProjects.filter(project => project.status === 'On Hold');
        break;
      case 'overdue':
        filteredProjects = filteredProjects.filter(project => 
          project.dateStatus.status === 'overdue'
        );
        break;
      case 'at-risk':
        filteredProjects = filteredProjects.filter(project => 
          project.dateStatus.status === 'at-risk'
        );
        break;
      case 'ending-soon':
        filteredProjects = filteredProjects.filter(project => 
          project.dateStatus.status === 'ending-soon'
        );
        break;
      default:
        // 'all' - no filtering
        break;
    }
    
    // Sort by name by default
    filteredProjects.sort((a, b) => a.name.localeCompare(b.name));
    
    return filteredProjects;
  };
  
  const filteredProjects = applyFiltersAndSort();
  
  // Get status color
  const getStatusColor = (status) => {
    switch(status) {
      case 'Active': return 'primary';
      case 'Planning': return 'info';
      case 'Completed': return 'success';
      case 'On Hold': return 'warning';
      case 'At Risk': return 'warning';
      case 'Delayed': return 'error';
      case 'Not Started': return 'default';
      default: return 'default';
    }
  };

  // Get date status icon
  const getDateStatusIcon = (dateStatus) => {
    switch(dateStatus.status) {
      case 'overdue':
        return <ErrorIcon sx={{ fontSize: 16, color: 'error.main', mr: 0.5 }} />;
      case 'at-risk':
        return <WarningIcon sx={{ fontSize: 16, color: '#f59e0b', mr: 0.5 }} />;
      case 'ending-soon':
        return <WarningIcon sx={{ fontSize: 16, color: 'warning.main', mr: 0.5 }} />;
      case 'starting-soon':
        return <InfoIcon sx={{ fontSize: 16, color: 'info.main', mr: 0.5 }} />;
      default:
        return null;
    }
  };

  // Get date status chip color
  const getDateStatusChipColor = (dateStatus) => {
    switch(dateStatus.status) {
      case 'overdue':
        return 'error';
      case 'at-risk':
        return 'warning';
      case 'ending-soon':
        return 'warning';
      case 'starting-soon':
        return 'info';
      default:
        return 'default';
    }
  };

  // Get filter button text
  const getFilterButtonText = () => {
    switch(filterType) {
      case 'all': return 'All Projects';
      case 'starred': return 'Starred';
      case 'active': return 'Active';
      case 'planning': return 'Planning';
      case 'completed': return 'Completed';
      case 'on-hold': return 'On Hold';
      case 'overdue': return 'Overdue';
      case 'at-risk': return 'At Risk';
      case 'ending-soon': return 'Ending Soon';
      default: return 'Filter';
    }
  };

  // Handle project selection for navigation
  const handleProjectSelect = (projectId) => {
    navigate(`/project/${projectId}/roadmap`);
  };

  // Count projects with warnings
  const overdueCount = projects.filter(p => getProjectDateStatus(p).status === 'overdue').length;
  const endingSoonCount = projects.filter(p => getProjectDateStatus(p).status === 'ending-soon').length;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center" fontWeight='bold'>All Projects</Typography>
        <Button 
          variant="contained" 
          color="primary" 
          startIcon={<AddIcon />}
          onClick={() => navigate('/create-project')}
        >
          Create Project
        </Button>
      </Box>
      
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <TextField
            placeholder="Search projects"
            variant="outlined"
            size="small"
            value={searchText}
            onChange={handleSearch}
            sx={{ width: '400px' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          
          <Box>
            <Button 
              variant="outlined" 
              startIcon={<FilterListIcon />}
              onClick={handleFilterMenuOpen}
              sx={{ mr: 1 }}
            >
              {getFilterButtonText()}
            </Button>
            <Menu
              anchorEl={filterAnchorEl}
              open={Boolean(filterAnchorEl)}
              onClose={() => handleFilterMenuClose()}
            >
              <MenuItem onClick={() => handleFilterMenuClose('all')}>All Projects</MenuItem>
              <MenuItem onClick={() => handleFilterMenuClose('starred')}>Starred</MenuItem>
              <Divider />
              <MenuItem onClick={() => handleFilterMenuClose('active')}>Active</MenuItem>
              <MenuItem onClick={() => handleFilterMenuClose('planning')}>Planning</MenuItem>
              <MenuItem onClick={() => handleFilterMenuClose('completed')}>Completed</MenuItem>
              <MenuItem onClick={() => handleFilterMenuClose('on-hold')}>On Hold</MenuItem>
              <Divider />
              <MenuItem onClick={() => handleFilterMenuClose('overdue')} sx={{ color: 'error.main' }}>
                <ErrorIcon sx={{ mr: 1, fontSize: 20 }} /> Overdue
              </MenuItem>
              <MenuItem onClick={() => handleFilterMenuClose('at-risk')} sx={{ color: '#d97706' }}>
                <WarningIcon sx={{ mr: 1, fontSize: 20, color: '#f59e0b' }} /> At Risk
              </MenuItem>
              <MenuItem onClick={() => handleFilterMenuClose('ending-soon')} sx={{ color: 'warning.main' }}>
                <WarningIcon sx={{ mr: 1, fontSize: 20 }} /> Ending Soon
              </MenuItem>
            </Menu>
          </Box>
        </Box>
        
        <Typography variant="subtitle1" color="textSecondary" sx={{ mb: 2 }}>
          Showing {filteredProjects.length} of {projects.length} projects
        </Typography>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        ) : (
          <Grid container spacing={3}>
            {filteredProjects.map(project => (
              <Grid item xs={12} sm={6} md={4} key={project.id}>
                <Card 
                  sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                    },
                    position: 'relative',
                    border: project.dateStatus.status === 'overdue' ? '2px solid #f44336' : 
                             project.dateStatus.status === 'at-risk' ? '2px solid #f59e0b' :
                             project.dateStatus.status === 'ending-soon' ? '2px solid #ff9800' : 
                             'none'
                  }}
                >
                  {/* Date Warning Banner */}
                  {project.dateStatus.status !== 'on-track' && project.dateStatus.status !== 'completed' && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bgcolor: project.dateStatus.severity === 'error' ? 'error.main' :
                                project.dateStatus.status === 'at-risk' ? '#d97706' :
                                project.dateStatus.severity === 'warning' ? 'warning.main' :
                                'info.main',
                        color: 'white',
                        py: 0.5,
                        px: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderTopLeftRadius: 4,
                        borderTopRightRadius: 4,
                        zIndex: 1
                      }}
                    >
                      {getDateStatusIcon(project.dateStatus)}
                      <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                        {project.dateStatus.message}
                      </Typography>
                    </Box>
                  )}

                  <CardContent sx={{ flexGrow: 1, pt: project.dateStatus.status !== 'on-track' && project.dateStatus.status !== 'completed' ? 5 : 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar 
                          sx={{ 
                            bgcolor: project.avatar_bg,
                            width: 36, 
                            height: 36,
                            mr: 1.5,
                            fontSize: '0.875rem'
                          }}
                        >
                          {project.key}
                        </Avatar>
                        <Box>
                          <Typography variant="h6" component="h2" sx={{ mb: 0.5 }}>
                            {project.name}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {project.type}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton 
                          size="small" 
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleStar(project.id);
                          }}
                          color={project.starred ? "warning" : "default"}
                        >
                          {project.starred ? <StarIcon /> : <StarBorderIcon />}
                        </IconButton>
                        <IconButton 
                          size="small" 
                          onClick={(e) => handleMenuOpen(e, project)}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </Box>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    {/* Status and Priority Row */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Chip 
                        label={project.status} 
                        size="small"
                        color={getStatusColor(project.status)}
                        sx={{ height: 24 }}
                      />
                      <Chip 
                        label={project.priority} 
                        size="small"
                        color={
                          project.priority === 'High' ? 'error' : 
                          project.priority === 'Medium' ? 'warning' : 'success'
                        }
                        variant="outlined"
                        sx={{ height: 24 }}
                      />
                    </Box>

                    {/* Progress Bar */}
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="textSecondary">
                          Progress
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {project.progress}%
                        </Typography>
                      </Box>
                      <Box sx={{
                        width: '100%',
                        height: 8,
                        bgcolor: '#f0f0f0',
                        borderRadius: 4
                      }}>
                        <Box
                          sx={{
                            width: `${project.progress}%`,
                            height: '100%',
                            bgcolor: project.dateStatus.status === 'overdue' ? '#f44336' :
                                    project.dateStatus.status === 'at-risk' ? '#f59e0b' :
                                    project.progress < 30 ? '#ff9800' :
                                    project.progress < 70 ? '#2196f3' :
                                    '#4caf50',
                            borderRadius: 4,
                            transition: 'width 0.3s ease-in-out'
                          }}
                        />
                      </Box>
                    </Box>

                    {/* Dates Row with Warnings */}
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <CalendarIcon sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                          <Typography variant="body2" color="textSecondary">
                            Start: {formatDisplayDate(project.startDate)}
                          </Typography>
                        </Box>
                        {project.dateStatus.status === 'starting-soon' && (
                          <Chip
                            size="small"
                            icon={<InfoIcon />}
                            label="Starting Soon"
                            color="info"
                            sx={{ height: 20, '& .MuiChip-label': { px: 1, fontSize: '0.625rem' } }}
                          />
                        )}
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <TodayIcon sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                          <Typography variant="body2" color="textSecondary">
                            End: {formatDisplayDate(project.endDate)}
                          </Typography>
                        </Box>
                        {(project.dateStatus.status === 'overdue' || project.dateStatus.status === 'at-risk' || project.dateStatus.status === 'ending-soon') && (
                          <Chip
                            size="small"
                            icon={project.dateStatus.status === 'overdue' ? <ErrorIcon /> : <WarningIcon />}
                            label={project.dateStatus.message}
                            color={getDateStatusChipColor(project.dateStatus)}
                            sx={{ height: 20, '& .MuiChip-label': { px: 1, fontSize: '0.625rem' } }}
                          />
                        )}
                      </Box>
                    </Box>

                    {/* Overdue / At-Risk Reason Suggestion Box (Only shown if user can give reason OR if reason already exists) */}
                    {(project.dateStatus.status === 'overdue' || project.dateStatus.status === 'at-risk') && (canGiveReason || (project.reason || project.notes)) && (
                      <Box
                        sx={{
                          mt: 1.5,
                          mb: 1.5,
                          p: 1.2,
                          borderRadius: '8px',
                          backgroundColor: project.dateStatus.status === 'overdue' ? '#fef2f2' : '#fffbeb',
                          border: `1px dashed ${project.dateStatus.status === 'overdue' ? '#fca5a5' : '#fcd34d'}`,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.8
                        }}
                      >
                        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={0.5}>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <LightbulbIcon sx={{ fontSize: 16, color: project.dateStatus.status === 'overdue' ? '#dc2626' : '#d97706' }} />
                            <Typography variant="caption" sx={{ fontWeight: 700, color: project.dateStatus.status === 'overdue' ? '#991b1b' : '#92400e' }}>
                              {(project.reason || project.notes) ? 'Reason Provided' : `Action Suggested: Give Reason for ${project.dateStatus.status === 'overdue' ? 'Delay' : 'Risk'}`}
                            </Typography>
                          </Box>
                          {canGiveReason && (
                            <Button
                              size="small"
                              variant="text"
                              startIcon={<EditNoteIcon sx={{ fontSize: 16 }} />}
                              onClick={(e) => handleOpenReasonModal(e, project)}
                              sx={{
                                fontSize: '0.72rem',
                                fontWeight: 700,
                                py: 0.2,
                                px: 0.8,
                                minWidth: 'auto',
                                textTransform: 'none',
                                color: project.dateStatus.status === 'overdue' ? '#b91c1c' : '#b45309',
                                backgroundColor: project.dateStatus.status === 'overdue' ? '#fee2e2' : '#fef3c7',
                                '&:hover': {
                                  backgroundColor: project.dateStatus.status === 'overdue' ? '#fecaca' : '#fde68a'
                                }
                              }}
                            >
                              {(project.reason || project.notes) ? 'Edit Reason' : '+ Give Reason'}
                            </Button>
                          )}
                        </Box>
                        {(project.reason || project.notes) ? (
                          <Typography variant="caption" sx={{ color: '#334155', fontStyle: 'italic', pl: 0.5, whiteSpace: 'pre-line', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                            "{project.reason || project.notes}"
                          </Typography>
                        ) : (
                          <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem', pl: 0.5 }}>
                            Click "+ Give Reason" to explain why this project is {project.dateStatus.status === 'overdue' ? 'overdue' : 'at risk'}.
                          </Typography>
                        )}
                      </Box>
                    )}

                    {/* Team Members (Optional) */}
                    {project.team && project.team.length > 0 && (
                      <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
                        <GroupIcon sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                        <AvatarGroup max={3} sx={{ '& .MuiAvatar-root': { width: 24, height: 24, fontSize: '0.75rem' } }}>
                          {project.team.map((member, index) => (
                            <Avatar key={index} src={member.avatar} sx={{ width: 24, height: 24 }}>
                              {member.name?.charAt(0)}
                            </Avatar>
                          ))}
                        </AvatarGroup>
                      </Box>
                    )}
                  </CardContent>

                  <CardActions sx={{ justifyContent: 'flex-end', p: 2, pt: 0 }}>
                    <Button 
                      size="small" 
                      onClick={() => handleViewProject(project.id)}
                    >
                      View Details
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {filteredProjects.length === 0 && !loading && (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="h6" color="textSecondary">
              No projects found
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {searchText ? 'Try adjusting your search criteria' : 'Create your first project to get started'}
            </Typography>
          </Paper>
        )}
      </Paper>
      
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          handleMenuClose();
          if (menuProject) handleViewProject(menuProject.id);
        }}>View details</MenuItem>
        {isProjectManager && (
          <MenuItem onClick={() => {
            handleMenuClose();
            if (menuProject) handleEditProject(menuProject);
          }}>Edit</MenuItem>
        )}
      </Menu>
      
      {/* Project Details Dialog */}
      <ProjectDetailsDialog 
        open={dialogOpen}
        handleClose={handleCloseDialog}
        projectId={selectedProjectId}
      />

      {/* Dialog for Providing Reason */}
      <Dialog
        open={Boolean(reasonModalProject)}
        onClose={handleCloseReasonModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', p: 1 } }}
      >
        {reasonModalProject && (
          <>
            <DialogTitle sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#0f172a' }}>
              Provide Reason for {reasonModalProject.name} ({reasonModalProject.key})
            </DialogTitle>
            <DialogContent sx={{ pt: 1 }}>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                This project is currently marked as{' '}
                <strong style={{ color: reasonModalProject.dateStatus?.status === 'overdue' ? '#dc2626' : '#d97706' }}>
                  {reasonModalProject.dateStatus?.message || reasonModalProject.dateStatus?.status}
                </strong>
                . Please provide the reason or root cause for this status.
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                autoFocus
                placeholder="Type the delay or risk reason here (e.g., pending API integration, client approval blocker, resource shortage)..."
                value={reasonText}
                onChange={(e) => setReasonText(e.target.value)}
                variant="outlined"
                size="small"
              />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={handleCloseReasonModal} color="inherit" sx={{ textTransform: 'none' }}>
                Cancel
              </Button>
              <Button
                onClick={handleSaveReason}
                variant="contained"
                color="primary"
                disabled={savingReason}
                sx={{ textTransform: 'none', fontWeight: 700 }}
              >
                {savingReason ? <CircularProgress size={20} color="inherit" /> : 'Save Reason'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
      
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AllProjects;